import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Smartphone, RefreshCw, Power, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeCanvas } from 'qrcode.react';

const WhatsAppSettings = () => {
  const [status, setStatus] = useState({ ready: false, qrCode: null, info: null });
  const [loading, setLoading] = useState(false);

  const checkStatus = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/status`);
      const data = await response.json();
      setStatus(data);
    } catch (error) {
      console.error('Error checking WhatsApp status:', error);
    }
  };

  useEffect(() => {
    checkStatus();
    const interval = setInterval(checkStatus, 3000); // Check every 3 seconds
    return () => clearInterval(interval);
  }, []);

  const handleReconnect = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/reconnect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Reconnecting WhatsApp...');
        setTimeout(checkStatus, 2000);
      } else {
        toast.error('Failed to reconnect');
      }
    } catch (error) {
      toast.error('Error reconnecting WhatsApp');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/disconnect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        toast.success('WhatsApp disconnected');
        checkStatus();
      } else {
        toast.error('Failed to disconnect');
      }
    } catch (error) {
      toast.error('Error disconnecting WhatsApp');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk' }}>
            WhatsApp Integration
          </h1>
          <p className="text-slate-600 mt-1">
            Kelola koneksi WhatsApp untuk notifikasi transaksi otomatis
          </p>
        </div>
      </div>

      {/* Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone size={20} />
            Status Koneksi
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Connection Status */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                {status.ready ? (
                  <CheckCircle className="text-green-600" size={24} />
                ) : (
                  <XCircle className="text-red-600" size={24} />
                )}
                <div>
                  <p className="font-semibold text-slate-800">
                    {status.ready ? 'Terhubung' : 'Tidak Terhubung'}
                  </p>
                  {status.info && (
                    <p className="text-sm text-slate-600">
                      Terhubung sebagai: {status.info.pushname}
                    </p>
                  )}
                </div>
              </div>
              <Badge variant={status.ready ? 'default' : 'destructive'}>
                {status.ready ? 'ACTIVE' : 'INACTIVE'}
              </Badge>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                onClick={handleReconnect}
                disabled={loading}
                variant="outline"
                className="flex items-center gap-2"
              >
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                Reconnect
              </Button>
              {status.ready && (
                <Button
                  onClick={handleDisconnect}
                  disabled={loading}
                  variant="destructive"
                  className="flex items-center gap-2"
                >
                  <Power size={16} />
                  Disconnect
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* QR Code Card */}
      {status.qrCode && !status.ready && (
        <Card>
          <CardHeader>
            <CardTitle>Scan QR Code</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center space-y-4">
              <div className="p-6 bg-white rounded-lg shadow-lg">
                <QRCodeCanvas value={status.qrCode} size={300} />
              </div>
              <div className="text-center space-y-2">
                <p className="font-semibold text-slate-800">
                  Scan QR Code dengan WhatsApp
                </p>
                <ol className="text-sm text-slate-600 text-left space-y-1">
                  <li>1. Buka WhatsApp di ponsel Anda</li>
                  <li>2. Tap Menu atau Pengaturan</li>
                  <li>3. Pilih "Linked Devices" atau "Perangkat Tertaut"</li>
                  <li>4. Tap "Link a Device" atau "Tautkan Perangkat"</li>
                  <li>5. Scan QR code di atas</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card>
        <CardHeader>
          <CardTitle>Informasi</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm text-slate-600">
            <p>
              ✅ Notifikasi WhatsApp otomatis akan dikirim ke nomor HP mitra saat ada
              transaksi di rekening mitra.
            </p>
            <p>
              ✅ Transaksi yang dinotifikasikan: Semua transaksi masuk (IN) dan keluar
              (OUT).
            </p>
            <p>
              ⚠️ Pastikan nomor HP mitra tersimpan dengan format 08xxx di database.
            </p>
            <p>
              ⚠️ Koneksi WhatsApp harus selalu aktif agar notifikasi terkirim.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WhatsAppSettings;
